import type { PlanView } from './PricingCatalog';
import {
  ANNUAL_DISCOUNT_RATE,
  CREDITOS_BIENVENIDA,
  PRICING_CONTENT,
  PRICING_PLAN_KEYS,
  ULTRA_TIER_CREDITS,
  lineaImagenes,
  lineaVideos,
  type PricingPlanKey,
} from './pricingContent';

/**
 * Arma las cards del catálogo (precio vigente, tachado, descuento, bonus, CTA y
 * estado) a partir del plan del usuario, el tier de Ultra y el cupón. Ver ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales.
 *
 * Recibe `getPlanPrice` y `formatPrice` **por parámetro** en vez de importarlos:
 * hoy los dueños de esa lógica son las copias inline de `Paywall` (país → tabla de
 * precios → `Intl.NumberFormat`), y el rediseño NO las tocó para no arriesgar un
 * cambio de precios. Inyectarlas mantiene esta función pura y verificable con
 * cualquier implementación (ver `docs/` del rediseño).
 */

export interface UltraTier {
  images: number;
  videos: number;
  price: number;
  planId: string;
}

export interface BuildPlanViewsArgs {
  /** `company_profiles.plan` — `gratis` si no paga. */
  currentPlan: string;
  ultraTiers: UltraTier[];
  ultraTierIndex: number;
  /** Cupón aplicado (Hot Sale invisible o uno cargado a mano). */
  coupon: { multiplicador: number } | null;
  /** Precio base en ARS de una card no-Ultra (desde el catálogo compartido). */
  arsBaseFor: (key: PricingPlanKey) => number;
  /** Convierte (planId, precio ARS) → precio en la moneda del país. */
  getPlanPrice: (planId: string, arsPrice: number) => number;
  /** Formatea un monto a `$X ARS` / `$X.99 USD` / etc. */
  formatPrice: (amount: number) => string;
  /** Jerarquía de planes, para decidir upgrade/downgrade. */
  planOrder: Record<string, number>;
  processing: boolean;
  onCancel: () => void;
}

export function buildPlanViews({
  currentPlan,
  ultraTiers,
  ultraTierIndex,
  coupon,
  arsBaseFor,
  getPlanPrice,
  formatPrice,
  planOrder,
  processing,
  onCancel,
}: BuildPlanViewsArgs): PlanView[] {
  const tier = ultraTiers[ultraTierIndex];
  const rank = (id: string) => planOrder[id] ?? 0;

  /**
   * ¿Esta alta recibe los créditos de bienvenida? El servidor de pagos los acredita
   * en las altas **desde gratis**; un cambio entre planes pagos no los lleva. Sin
   * plan conocido se asume que sí (es el caso de quien todavía no pagó nunca).
   */
  const daBonusDeAlta = !currentPlan || currentPlan === 'gratis';

  /** planId real que se cobra por cada card (Ultra depende del tier del slider). */
  const planIdForKey = (key: PricingPlanKey) =>
    key === 'enterprise' ? tier.planId : key;

  // La comparación va contra el planId EFECTIVO, no contra la key de la card:
  // `enterprise` a secas rankea 3 y los tiers 3.1/3.2/3.3, así que comparar por
  // key hacía que un usuario de enterprise1 que sube a enterprise2 viera
  // "Cambiar a Ultra" (etiqueta de bajada) y perdiera el descuento. Con el tier
  // efectivo se comporta como las cards viejas, que ya mostraban un card por tier.
  const isUpgrade = (key: PricingPlanKey) =>
    rank(planIdForKey(key)) > rank(currentPlan);
  const isDowngrade = (key: PricingPlanKey) =>
    rank(planIdForKey(key)) < rank(currentPlan);

  return PRICING_PLAN_KEYS.map((key) => {
    const content = PRICING_CONTENT[key];
    const base = getPlanPrice(
      planIdForKey(key),
      key === 'enterprise' ? tier.price : arsBaseFor(key),
    );

    // Mismas reglas de elegibilidad que ya tenían las cards viejas: hay cupón,
    // el plan NO es el Básico, y para el usuario es un upgrade.
    const discountEligible =
      !!coupon && (key === 'negocio' || key === 'enterprise') && isUpgrade(key);
    const discounted = discountEligible
      ? Math.floor(base * coupon.multiplicador)
      : null;

    // La card de Ultra es la del tier elegido en el slider, así que "tu plan"
    // sólo aplica si coincide el tier (si no, cambiar de tier sería imposible).
    // `enterprise` a secas es el id legacy sin sufijo → equivale al primer tier.
    const isCurrent =
      key === 'enterprise'
        ? currentPlan === planIdForKey(key) ||
          (currentPlan === 'enterprise' && ultraTierIndex === 0)
        : currentPlan === key;

    const canCancel = isCurrent && currentPlan !== 'gratis';

    return {
      key,
      content,
      credits:
        key === 'enterprise' ? ULTRA_TIER_CREDITS[ultraTierIndex] : content.credits,
      // Ultra: las dos líneas salen de los créditos del TIER elegido, con el mismo
      // criterio que el resto del catálogo (imágenes exactas, videos con "hasta"
      // sobre el piso de 5 créditos). Antes usaban `tier.images`/`tier.videos` de
      // `ULTRA_TIERS`, una tabla aparte cuyo campo `videos` estaba calculado sobre
      // ~12 créditos por video y quedó desfasado del resto.
      // Ver ADR-2026-08-03-videos-por-plan-se-calculan-con-el-piso-real.
      generates:
        key === 'enterprise'
          ? [
              lineaImagenes(ULTRA_TIER_CREDITS[ultraTierIndex]),
              lineaVideos(ULTRA_TIER_CREDITS[ultraTierIndex]),
            ]
          : content.generates,
      price: formatPrice(discounted ?? base),
      period: discounted !== null ? 'el primer mes' : 'por mes',
      // Equivalente anual — PLACEHOLDER de diseño (ADR-2026-07-28-switch-mensual-anual-visual-solo): se deriva del mensual
      // porque no existe un precio anual real. Sólo se muestra a `dev_tester`.
      annual: {
        total: formatPrice(Math.round(base * 12 * (1 - ANNUAL_DISCOUNT_RATE))),
        perMonth: formatPrice(Math.round(base * (1 - ANNUAL_DISCOUNT_RATE))),
      },
      strikePrice: discounted !== null ? formatPrice(base) : undefined,
      discountLabel:
        discounted !== null && coupon
          ? `${Math.round((1 - coupon.multiplicador) * 100)}% OFF`
          : undefined,
      // Extra del primer mes (`activationCredits` del servidor de pagos): sale de la
      // tabla `CREDITOS_BIENVENIDA` y se muestra en TODAS las cards cuando el alta
      // viene de gratis. Antes colgaba de `discounted !== null`, o sea del cupón, y
      // como Básico no es elegible para el cupón su bonus nunca se mostraba pese a
      // existir (ADR-2026-08-03-bonus-de-alta-no-depende-del-cupon).
      bonusLabel: daBonusDeAlta
        ? `+${CREDITOS_BIENVENIDA[key]} créditos extra de bienvenida`
        : undefined,
      isCurrent,
      ctaLabel: isCurrent
        ? 'Tu plan actual'
        : currentPlan && currentPlan !== 'gratis' && isDowngrade(key)
          ? `Cambiar a ${content.tab}`
          : content.cta,
      ctaDisabled: processing || isCurrent,
      secondaryLabel: canCancel ? 'Cancelar suscripción' : undefined,
      onSecondary: canCancel ? onCancel : undefined,
    };
  });
}
